import { tr, getCurrentLang, uiLocale } from "../i18n/LanguageContext";
import { setPageMeta, summarize } from "../i18n/seo";
import React, { useState, useEffect } from 'react';
import { apiErrorMessage } from "../lib/apiError";
import { useParams } from "react-router-dom";
import { useNavigate } from "../i18n/router";
import { useAuth } from '../contexts/AuthContext';
import EventTypeBadge from '../components/EventTypeBadge';
import EventLocation from '../components/EventLocation';
import { Loader2, Users, Hourglass, Euro, Check, XCircle, AlertCircle, Calendar as CalendarIcon, MessageCircle, Star } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import MessageDialog from '../components/MessageDialog';
import { CancelEventButton, CancelPendingBanner } from '../components/CancelEventControls';
import EventImage from '../components/EventImage';

import { formatEventPrice, getEffectivePricingType, PRICING_TYPES } from "../data/currencies";
import { practiceTypes } from "../data/mock";
import { useEventLanguages } from "../hooks/useEventLanguages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PriceBadge({ event }) {
  const type = getEffectivePricingType(event);
  const label = formatEventPrice(event);
  if (type === PRICING_TYPES.FREE) {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
        style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
        data-testid="event-price-free">
        {label}
      </span>
    );
  }
  if (type === PRICING_TYPES.DONATION) {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
        style={{ backgroundColor: "#FBE4E4", color: "#B23A48" }}
        data-testid="event-price-donation">
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}
      data-testid="event-price-paid">
      {label}
    </span>
  );
}

const practiceLabel = (slug) => tr(practiceTypes.find((p) => p.value === slug)?.label || slug);

function getInitials(name) {
  return (name || "?").split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// Russian pluralization: forms = [singular, few (2-4), many (5+/0)]
// Example: getRussianPlural(3, ['место', 'места', 'мест']) -> 'места'
function getRussianPlural(count, forms) {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return forms[2];
  if (n1 > 1 && n1 < 5) return forms[1];
  if (n1 === 1) return forms[0];
  return forms[2];
}

function EventDetailsPage() {
  const { getLanguageLabel } = useEventLanguages();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (!event) return;
    const date = new Date(`${event.date}T00:00:00`).toLocaleDateString(uiLocale(), { day: "numeric", month: "short" });
    const place = (event.type || "").toLowerCase() === "online" ? tr("Онлайн") : tr(event.city || "");
    setPageMeta({
      title: `${event.title} — ${[date, place].filter(Boolean).join(", ")} | EUNOIA`,
      description: summarize(event.description),
    });
  }, [event]);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState(null);
  const [attendance, setAttendance] = useState({
    goingCount: 0,
    interestedCount: 0,
    requestedCount: 0,
    publicAttendees: []
  });
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      setEventLoading(true);
      setEventError(null);
      try {
        const response = await fetch(`${BACKEND_URL}/api/events/${id}`);
        if (!response.ok) {
          setEventError(response.status === 404 ? tr("Событие не найдено") : tr("Ошибка загрузки события"));
          return;
        }
        setEvent(await response.json());
      } catch {
        setEventError(tr("Ошибка загрузки события"));
      } finally {
        setEventLoading(false);
      }
    };
    fetchEvent();
    fetchAttendance();
    if (user) fetchUserStatus();
  }, [id, user]);

  const fetchAttendance = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${id}/attendance`);
      if (response.ok) setAttendance(await response.json());
    } catch (error) { console.error(error); }
  };

  const fetchUserStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/user/${id}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data.status);
      }
    } catch (error) { console.error(error); }
  };

  const handleAttendance = async (status) => {
    if (!user) {
      navigate(`/login?redirect=/events/${id}&intent=event_attendance`);
      return;
    }

    // Optimistic counter update — adjust local counts immediately based on the
    // status transition. Server will confirm via fetchAttendance and overwrite.
    const prevStatus = userStatus;
    const wasGoing = ["confirmed", "paid", "attended"].includes(prevStatus);
    const wasInterested = prevStatus === "interested";
    // Predict the server-resolved next status. For "register" the server may map
    // to confirmed or requested depending on event mode — we'll fall back on
    // fetchAttendance's authoritative numbers shortly. Optimistically assume the
    // most common case (free + auto = confirmed; paid/manual = requested).
    let predicted = status;
    if (status === "register") {
      predicted = (event?.isFree !== false && event?.approvalMode !== "manual") ? "confirmed" : "requested";
    }
    const willBeGoing = ["confirmed", "paid", "attended"].includes(predicted);
    const willBeInterested = predicted === "interested";
    setAttendance((a) => ({
      ...a,
      goingCount: Math.max(0, (a.goingCount || 0) + (willBeGoing ? 1 : 0) - (wasGoing ? 1 : 0)),
      interestedCount: Math.max(0, (a.interestedCount || 0) + (willBeInterested ? 1 : 0) - (wasInterested ? 1 : 0)),
    }));
    setUserStatus(predicted);

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ eventId: id, status }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setUserStatus(data.status || status);
        // Reconcile with server (authoritative numbers)
        await fetchAttendance();
        const msg = data.status === 'requested' ? tr("Заявка отправлена организатору")
          : data.status === 'confirmed' ? tr("Вы записаны на событие")
          : data.status === 'interested' ? tr("Добавлено в «Интересно»")
          : data.status === 'cancelled' ? tr("Запись отменена")
          : tr("Статус обновлен");
        toast({ title: msg });
      } else if (response.status === 401) {
        navigate('/login');
      } else {
        // Rollback optimistic update
        setUserStatus(prevStatus);
        await fetchAttendance();
        toast({ title: tr("Ошибка"), description: apiErrorMessage(data, tr("Не удалось обновить статус")), variant: 'destructive' });
      }
    } catch {
      setUserStatus(prevStatus);
      await fetchAttendance();
      toast({ title: tr("Ошибка"), description: tr("Сеть недоступна"), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (eventLoading) {
    return (
      <div className="min-h-screen eunoia-space-lg">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--eunoia-accent)' }} />
          <span className="ml-3 eunoia-body">{tr("Загрузка события...")}</span>
        </div>
      </div>
    );
  }

  if (eventError || !event) {
    return (
      <div className="min-h-screen eunoia-space-lg">
        <div className="max-w-4xl mx-auto px-6">
          <button onClick={() => navigate('/events')} className="mb-6 eunoia-caption flex items-center gap-2 hover:opacity-80" style={{ color: 'var(--eunoia-soft)' }}>
            {tr("← Назад к событиям")}
          </button>
          <div className="eunoia-surface p-12 text-center">
            <p className="eunoia-body mb-4">{eventError || tr("Событие не найдено")}</p>
            <button onClick={() => navigate('/events')} className="eunoia-btn-primary">{tr("К списку событий")}</button>
          </div>
        </div>
      </div>
    );
  }

  // Derived flags
  const today = new Date().toISOString().split('T')[0];
  const isPast = event.date < today;
  const deadlinePassed = event.registrationDeadline && event.registrationDeadline < today;
  const capacity = event.maxAttendees || event.capacity;
  const capacityFull = capacity && attendance.goingCount >= capacity;
  const isFree = event.isFree !== false;
  const isManual = event.approvalMode === 'manual';
  const spotsLeft = capacity ? Math.max(0, capacity - attendance.goingCount) : null;
  // Manager check: primary organizer, any co-organizer, or admin.
  const coOrganizerIds = Array.isArray(event.coOrganizerIds) ? event.coOrganizerIds : [];
  const coOrganizers = Array.isArray(event.coOrganizers) ? event.coOrganizers : [];
  const isOrganizer = !!user && (event.organizerId === user.id || coOrganizerIds.includes(user.id) || user.role === 'admin');

  // Primary CTA label / behaviour
  const canSignUp = !isPast && !deadlinePassed && !capacityFull;
  let primaryLabel = tr("Записаться");
  if (!isFree) primaryLabel = tr("Запросить участие");
  else if (isManual) primaryLabel = tr("Запросить участие");

  // Current status display
  const statusBadge = (() => {
    if (!userStatus) return null;
    const map = {
      confirmed: { label: tr("✓ Вы записаны"), color: 'var(--eunoia-accent)', bg: 'var(--eunoia-accent-whisper)' },
      requested: { label: tr("Ожидает подтверждения"), color: '#92400E', bg: '#FEF3C7' },
      paid: { label: tr("✓ Оплачено"), color: '#065F46', bg: '#D1FAE5' },
      interested: { label: tr("Интересно"), color: 'var(--eunoia-soft)', bg: 'var(--eunoia-whisper)' },
      cancelled: { label: tr("Отменено"), color: '#991B1B', bg: '#FEE2E2' },
      attended: { label: tr("Посещено"), color: '#065F46', bg: '#D1FAE5' },
    };
    return map[userStatus];
  })();

  return (
    <div className="min-h-screen eunoia-space-lg pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <button onClick={() => navigate('/events')} className="mb-6 eunoia-caption flex items-center gap-2 hover:opacity-80" style={{ color: 'var(--eunoia-soft)' }}>
          {tr("← Назад к событиям")}
        </button>

        <div className="eunoia-surface overflow-hidden">
          {event.image && (
            <div className="w-full h-48 sm:h-64 overflow-hidden">
              <EventImage src={event.image} alt={event.title} />
            </div>
          )}
          {!event.image && (
            <div className="w-full h-32 sm:h-40 overflow-hidden">
              <EventImage src={null} alt={event.title} />
            </div>
          )}

          <div className="p-5 sm:p-8">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <h1 className="eunoia-title-large">{event.title}</h1>
                <div className="flex gap-2 flex-wrap">
                  <EventTypeBadge type={event.type} />
                  <PriceBadge event={event} />
                </div>
              </div>

              {/* Practice tags — render every practice attached to the event,
                  falling back to the legacy single `practice` field for older
                  events that predate the multi-practice migration. */}
              {(() => {
                const list = (Array.isArray(event.practices) && event.practices.length)
                  ? event.practices
                  : (event.practice ? [event.practice] : []);
                const langs = Array.isArray(event.languages) ? event.languages : [];
                if (!list.length && !langs.length) return null;
                return (
                  <div className="flex flex-wrap gap-2 mb-4" data-testid="event-practice-tags">
                    {list.map((slug) => (
                      <span
                        key={slug}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
                        data-testid={`event-practice-tag-${slug}`}
                      >
                        {practiceLabel(slug)}
                      </span>
                    ))}
                    {langs.map((code) => (
                      <span
                        key={`lang-${code}`}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
                        data-testid={`event-language-tag-${code}`}
                      >
                        {getLanguageLabel(code)}
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Preserve paragraph breaks the organizer typed (Task 5).
                  `whitespace-pre-wrap` renders \n as a real line break and
                  collapses only runs of spaces, matching typical rich-text
                  expectations from a plain textarea. */}
              <p className="eunoia-body-large whitespace-pre-wrap" data-testid="event-description">
                {event.description}
              </p>

              {/* Live participant pulse — keeps the page feeling alive */}
              {(attendance.goingCount > 0 || attendance.interestedCount > 0) && (
                <div className="flex items-center gap-4 mt-4 text-sm" data-testid="live-pulse">
                  {attendance.goingCount > 0 && (
                    <span className="inline-flex items-center gap-1.5" style={{ color: "var(--eunoia-soft)" }}>
                      <Users className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} />
                      <span style={{ color: "var(--eunoia-deep)", fontWeight: 500 }}>{attendance.goingCount}</span>
                      {attendance.goingCount === 1 ? tr("идёт") : tr("идут")}
                    </span>
                  )}
                  {attendance.interestedCount > 0 && (
                    <span className="inline-flex items-center gap-1.5" style={{ color: "var(--eunoia-soft)" }}>
                      <Star className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} />
                      <span style={{ color: "var(--eunoia-deep)", fontWeight: 500 }}>{attendance.interestedCount}</span>
                      {attendance.interestedCount === 1 ? tr("интересуется") : tr("интересуются")}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Event Details */}
            <div className="space-y-4 mb-8 pb-8 border-b" style={{ borderColor: 'var(--eunoia-whisper)' }}>
              <div className="flex items-center gap-3 eunoia-body">
                <CalendarIcon className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
                <span style={{ fontWeight: 500 }}>{getCurrentLang() === "en" ? new Date(`${event.date}T00:00:00`).toLocaleDateString(uiLocale(), { day: "numeric", month: "long", year: "numeric" }) : event.date} {tr("в")} {event.time}</span>
              </div>
              <EventLocation
                event={event}
                canJoin={
                  isOrganizer ||
                  ['confirmed', 'paid', 'attended'].includes(userStatus)
                }
              />
              {event.organizer && (
                <div className="flex items-center gap-3 eunoia-body">
                  <Users className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
                  <span className="flex flex-wrap items-center gap-x-1 gap-y-1">
                    <span>{coOrganizers.length > 0 ? tr("Организаторы:") : tr("Организатор:")}</span>
                    {/* Primary organizer */}
                    {event.organizerId ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/organizers/${event.organizerId}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/organizers/${event.organizerId}`); } }}
                        className="cursor-pointer hover:underline"
                        style={{ fontWeight: 500, color: "var(--eunoia-accent)" }}
                        data-testid="event-organizer-link"
                      >
                        {event.organizer}
                      </span>
                    ) : (
                      <span style={{ fontWeight: 500 }}>{event.organizer}</span>
                    )}
                    {/* Co-organizers, each clickable to their own profile */}
                    {coOrganizers.map((co, idx) => (
                      <React.Fragment key={co.userId}>
                        <span>,</span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/organizers/${co.userId}`)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/organizers/${co.userId}`); } }}
                          className="cursor-pointer hover:underline"
                          style={{ fontWeight: 500, color: "var(--eunoia-accent)" }}
                          data-testid={`event-co-organizer-link-${co.userId}`}
                        >
                          {co.displayName}
                        </span>
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              )}
              {event.registrationDeadline && !isPast && (
                <div className="flex items-center gap-3 eunoia-body">
                  <Hourglass className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
                  <span>{tr("Запись до:")} <span style={{ fontWeight: 500 }}>{event.registrationDeadline}</span></span>
                </div>
              )}
              {capacity && (
                <div className="flex items-center gap-3 eunoia-body" data-testid="event-capacity">
                  <Users className="h-5 w-5 flex-shrink-0" style={{ color: "var(--eunoia-accent)" }} />
                  <span className="min-w-0">
                    {capacityFull ? (
                      <span style={{ fontWeight: 500, color: "var(--eunoia-deep)" }} data-testid="capacity-full-label">
                        {tr("Мест больше нет")}
                      </span>
                    ) : (
                      <>
                        <span style={{ fontWeight: 500 }} data-testid="capacity-remaining-label">
                          {getCurrentLang() === "en" ? `${spotsLeft} ${spotsLeft === 1 ? "spot" : "spots"} left` : `Осталось ${spotsLeft} ${getRussianPlural(spotsLeft, ["место", "места", "мест"])}`}
                        </span>
                        <span className="ml-2" style={{ color: "var(--eunoia-soft)" }}>
                          {tr("из")} {capacity}
                        </span>
                        {spotsLeft > 0 && spotsLeft <= 5 && (
                          <span
                            className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
                            data-testid="capacity-low-badge"
                          >
                            {tr("Заканчиваются")}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Attendance CTA Block */}
            <div className="mb-8 pb-8 border-b" style={{ borderColor: 'var(--eunoia-whisper)' }}>
              {event.cancelPendingAt ? (
                <CancelPendingBanner
                  eventId={id}
                  pendingAt={event.cancelPendingAt}
                  isOrganizer={isOrganizer}
                  onRestored={() => setEvent((ev) => ({ ...ev, cancelPendingAt: null, cancelReason: null }))}
                />
              ) : isPast ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-500" data-testid="past-event-badge">
                      {tr("Прошедшее событие")}
                    </span>
                  </div>
                  <button disabled className="px-8 py-3 rounded-xl font-medium bg-gray-100 text-gray-400 cursor-not-allowed" data-testid="event-ended-btn">
                    {tr("Событие завершено")}
                  </button>
                </div>
              ) : isOrganizer ? (
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <h2 className="eunoia-title-medium mb-2">{tr("Вы — организатор")}</h2>
                    <p className="eunoia-caption" style={{ color: "var(--eunoia-soft)" }}>
                      {tr("Управляйте списком участников и подтверждайте заявки.")}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/events/${id}/edit`)}
                    data-testid="edit-event-btn"
                    className="eunoia-btn-secondary flex items-center gap-2"
                  >
                    {tr("Редактировать событие")}
                  </button>
                  <button
                    onClick={() => navigate(`/organizer/events/${id}/attendees`)}
                    data-testid="manage-attendees-btn"
                    className="eunoia-btn-primary flex items-center gap-2"
                  >
                    <Users className="h-4 w-4" /> {tr("Участники")} ({attendance.goingCount + attendance.requestedCount})
                  </button>
                  <CancelEventButton
                    eventId={id}
                    onCancelled={(data) => setEvent((ev) => ({ ...ev, cancelPendingAt: data.cancelPendingAt, cancelReason: data.cancelReason }))}
                  />
                </div>
              ) : (
                <div>
                  <h2 className="eunoia-title-medium mb-1">{tr("Участие")}</h2>
                  {!isFree && (
                    <p className="eunoia-caption mb-4" style={{ color: "var(--eunoia-soft)" }}>
                      {tr("Онлайн-оплата скоро — пока организатор подтвердит вашу заявку и свяжется с вами.")}
                    </p>
                  )}

                  {/* Status badge — hidden for 'interested' since the toggle button already shows the active state */}
                  {statusBadge && userStatus !== 'interested' && (
                    <div className="mb-4">
                      <span
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                        style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                        data-testid="user-status-badge"
                      >
                        {tr(statusBadge.label)}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:flex-wrap">
                    {deadlinePassed ? (
                      <button disabled className="w-full sm:w-auto px-6 sm:px-8 py-3 rounded-xl text-sm sm:text-base font-medium bg-gray-100 text-gray-400 cursor-not-allowed" data-testid="deadline-passed-btn">
                        {tr("Регистрация закрыта")}
                      </button>
                    ) : capacityFull && userStatus !== 'confirmed' && userStatus !== 'paid' && userStatus !== 'requested' ? (
                      <button disabled className="w-full sm:w-auto px-6 sm:px-8 py-3 rounded-xl text-sm sm:text-base font-medium bg-gray-100 text-gray-400 cursor-not-allowed" data-testid="no-spots-btn">
                        {tr("Мест нет")}
                      </button>
                    ) : (userStatus === 'confirmed' || userStatus === 'paid' || userStatus === 'requested') ? (
                      <button
                        onClick={() => handleAttendance('cancelled')}
                        disabled={loading}
                        data-testid="cancel-attendance-btn"
                        className="w-full sm:w-auto px-6 sm:px-8 py-3 rounded-xl text-sm sm:text-base font-medium border-2 transition-all hover:bg-white disabled:opacity-50 flex items-center justify-center gap-1"
                        style={{ borderColor: "#DC2626", color: "#DC2626", backgroundColor: "white" }}
                      >
                        <XCircle className="h-4 w-4" /> {tr("Отменить участие")}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAttendance('register')}
                        disabled={loading || !canSignUp}
                        data-testid="attendance-primary-btn"
                        className="eunoia-btn-primary disabled:opacity-50 flex items-center justify-center gap-2 w-full sm:w-auto"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        {primaryLabel}
                      </button>
                    )}

                    {userStatus !== 'confirmed' && userStatus !== 'paid' && userStatus !== 'requested' && (
                      <button
                        onClick={() => handleAttendance(userStatus === 'interested' ? 'cancelled' : 'interested')}
                        disabled={loading}
                        data-testid="attendance-interested-btn"
                        className="w-full sm:w-auto px-5 sm:px-6 py-3 rounded-xl text-sm sm:text-base font-medium border-2 transition-all hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2"
                        style={{
                          borderColor: userStatus === 'interested' ? 'var(--eunoia-accent)' : 'var(--eunoia-whisper)',
                          color: userStatus === 'interested' ? 'var(--eunoia-accent)' : 'var(--eunoia-deep)',
                          backgroundColor: userStatus === 'interested' ? 'var(--eunoia-accent-whisper)' : 'white'
                        }}
                      >
                        <Star className={`h-4 w-4 ${userStatus === 'interested' ? 'fill-current' : ''}`} />
                        {userStatus === 'interested' ? tr("Интересно") : tr("Интересно")}
                      </button>
                    )}
                  </div>

                  {!user && (
                    <p className="eunoia-caption mt-4 flex items-center gap-2" style={{ color: 'var(--eunoia-soft)' }}>
                      <AlertCircle className="h-4 w-4" /> {tr("Войдите, чтобы отметить своё участие")}
                    </p>
                  )}

                  {/* Contact organizer — always visible for non-organizers */}
                  {event.organizerId && (
                    <div className="mt-5 pt-5 border-t" style={{ borderColor: 'var(--eunoia-whisper)' }}>
                      <button
                        onClick={() => {
                          if (!user) {
                            navigate(`/login?redirect=/events/${id}&intent=contact_organizer`);
                            return;
                          }
                          setShowMessageDialog(true);
                        }}
                        data-testid="contact-organizer-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:bg-white"
                        style={{
                          borderColor: 'var(--eunoia-accent)',
                          color: 'var(--eunoia-accent)',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <MessageCircle className="h-4 w-4" />
                        {tr("Написать организатору")}
                      </button>
                      <p className="eunoia-caption mt-2" style={{ color: 'var(--eunoia-soft)' }}>
                        {tr("Задайте вопрос о событии или уточните детали")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Attendance Stats & Public Attendees */}
            <div>
              <h2 className="eunoia-title-medium mb-4 flex items-center gap-2" data-testid="participants-heading">
                <span aria-hidden="true">👥</span>
                <span>{tr("Участники")}</span>
              </h2>

              <div className="flex flex-row gap-3 mb-6 items-stretch">
                <div
                  className="eunoia-surface-soft flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3 min-w-0 flex-1 text-center sm:text-left transition-[transform,box-shadow] duration-300 ease-out hover:scale-[1.02] hover:shadow-md will-change-transform"
                  data-testid="counter-going"
                  style={{
                    borderLeft: "3px solid var(--eunoia-accent)",
                    margin: 0,
                    padding: "16px",
                  }}
                >
                  <Users className="h-6 w-6 flex-shrink-0" style={{ color: "var(--eunoia-accent)" }} />
                  <div className="min-w-0 flex-1 w-full">
                    <div
                      className="text-2xl sm:text-3xl font-semibold leading-none"
                      style={{ color: "var(--eunoia-deep)" }}
                      data-testid="counter-going-number"
                    >
                      {attendance.goingCount}
                    </div>
                    <div
                      className="text-xs sm:text-sm mt-1"
                      style={{ color: "var(--eunoia-soft)" }}
                    >
                      {attendance.goingCount === 1 ? tr("Идёт") : tr("Идут")}
                    </div>
                  </div>
                </div>

                {/* Clicking this card behaves identically to the main "Интересно" button.
                    Disabled when the user is already registered/confirmed so we don't conflict
                    with their active attendance state. */}
                <button
                  type="button"
                  onClick={() => {
                    if (isPast || isOrganizer) return;
                    if (['confirmed', 'paid', 'requested'].includes(userStatus)) return;
                    handleAttendance(userStatus === 'interested' ? 'cancelled' : 'interested');
                  }}
                  disabled={loading || isPast || isOrganizer || ['confirmed','paid','requested'].includes(userStatus)}
                  className="eunoia-surface-soft flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3 min-w-0 flex-1 text-center sm:text-left transition-[transform,box-shadow] duration-300 ease-out enabled:hover:scale-[1.02] enabled:hover:shadow-md enabled:hover:-translate-y-0.5 disabled:cursor-default will-change-transform"
                  data-testid="counter-interested"
                  aria-pressed={userStatus === 'interested'}
                  style={{
                    borderLeft: userStatus === 'interested'
                      ? "3px solid var(--eunoia-accent)"
                      : "3px solid var(--eunoia-whisper)",
                    backgroundColor: userStatus === 'interested' ? "var(--eunoia-accent-whisper)" : undefined,
                    margin: 0,
                    padding: "16px",
                  }}
                >
                  <Star
                    className={`h-6 w-6 flex-shrink-0 ${userStatus === 'interested' ? 'fill-current' : ''}`}
                    style={{ color: "var(--eunoia-accent)" }}
                  />
                  <div className="min-w-0 flex-1 w-full">
                    <div
                      className="text-2xl sm:text-3xl font-semibold leading-none"
                      style={{ color: "var(--eunoia-deep)" }}
                      data-testid="counter-interested-number"
                    >
                      {attendance.interestedCount}
                    </div>
                    <div
                      className="text-xs sm:text-sm mt-1"
                      style={{ color: "var(--eunoia-soft)" }}
                    >
                      {tr("Интересуются")}
                    </div>
                  </div>
                </button>
              </div>

              {attendance.publicAttendees.length > 0 && (
                <div>
                  <h3 className="eunoia-body mb-3" style={{ fontWeight: 500 }}>{tr("Открытые участники")}</h3>
                  <div className="flex flex-wrap gap-4">
                    {attendance.publicAttendees.map((attendee, index) => (
                      <div key={index} className="flex items-center gap-3 eunoia-surface-soft p-3" data-testid={`attendee-${index}`}>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                          style={{ backgroundColor: 'var(--eunoia-accent-whisper)' }}>
                          {attendee.avatarUrl ? (
                            <img src={attendee.avatarUrl} alt={attendee.displayName} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-lg font-medium" style={{ color: 'var(--eunoia-accent)' }}>
                              {getInitials(attendee.displayName)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="eunoia-body" style={{ fontWeight: 500 }}>{attendee.displayName}</p>
                          {attendee.city && <p className="eunoia-caption">{attendee.city}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact organizer modal */}
      {showMessageDialog && event.organizerId && (
        <MessageDialog
          organizer={{ userId: event.organizerId, name: event.organizer }}
          initialSubject={`${tr("Вопрос по событию:")} ${event.title}`}
          eventId={event.id}
          onClose={() => setShowMessageDialog(false)}
          onSent={() => toast({ title: tr("Сообщение отправлено организатору") })}
        />
      )}
    </div>
  );
}

export default EventDetailsPage;
